import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { App as AntApp, Button, Popconfirm, Space, Table } from "antd";
import {
  AppstoreOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  DeleteOutlined,
  DownloadOutlined,
  FilterOutlined,
  NumberOutlined,
  PlusOutlined,
  RightOutlined,
  SortAscendingOutlined,
  StopOutlined,
  TagOutlined,
  UnorderedListOutlined,
  UserOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";

import { api } from "../api";
import { ScraperTag, StatusPill } from "../components/ScraperTag";
import type { JobStatus, JobSummary, ScraperSchema } from "../types";

const PAGE_SIZE = 25;
const POLL_MS = 1500;

type FilterView = "all" | "done" | "running" | "failed";

const VIEWS: Array<{
  key: FilterView;
  label: string;
  dot: string;
  countKey: "total" | "done" | "live" | "failed";
}> = [
  { key: "all", label: "All tasks", dot: "var(--accent-blue)", countKey: "total" },
  { key: "done", label: "Completed", dot: "var(--accent-green)", countKey: "done" },
  { key: "running", label: "Running", dot: "var(--accent-blue)", countKey: "live" },
  { key: "failed", label: "Failed", dot: "var(--accent-red)", countKey: "failed" },
];

export function OutputPage() {
  const { message } = AntApp.useApp();
  const navigate = useNavigate();
  const [items, setItems] = useState<JobSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [view, setView] = useState<FilterView>("all");
  const [scrapers, setScrapers] = useState<ScraperSchema[]>([]);
  const [loading, setLoading] = useState(true);
  // Running summary for views-bar counters (fetched once per page refresh).
  const [summary, setSummary] = useState({ total: 0, done: 0, live: 0, failed: 0 });

  useEffect(() => {
    api.scrapers().then(({ scrapers }) => setScrapers(scrapers)).catch(() => undefined);
  }, []);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const statusFilter = viewToStatus(view);

    async function refresh() {
      try {
        const res = await api.listTasks({
          page,
          per_page: PAGE_SIZE,
          status: statusFilter,
        });
        if (cancelled) return;
        setItems(res.items);
        setTotal(res.total);
        setLoading(false);
        const hasActive = res.items.some((j) => j.status === "queued" || j.status === "running");
        timer = setTimeout(refresh, hasActive ? POLL_MS : POLL_MS * 4);
      } catch (err) {
        if (!cancelled) {
          setLoading(false);
          console.error(err);
        }
      }
    }
    refresh();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [page, view]);

  // Summary counts — fetch all views in parallel once per page load.
  useEffect(() => {
    let cancelled = false;
    async function refresh() {
      try {
        const [all, done, running, queued, failed] = await Promise.all([
          api.listTasks({ page: 1, per_page: 1 }),
          api.listTasks({ page: 1, per_page: 1, status: "done" }),
          api.listTasks({ page: 1, per_page: 1, status: "running" }),
          api.listTasks({ page: 1, per_page: 1, status: "queued" }),
          api.listTasks({ page: 1, per_page: 1, status: "failed" }),
        ]);
        if (cancelled) return;
        setSummary({
          total: all.total,
          done: done.total,
          live: running.total + queued.total,
          failed: failed.total,
        });
      } catch {
        /* ignore */
      }
    }
    refresh();
    const id = setInterval(refresh, POLL_MS * 3);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  async function handleCancel(row: JobSummary) {
    try {
      await api.cancelTask(row.id);
      message.info(`Task ${row.seq ?? row.id.slice(0, 8)} cancel requested`);
    } catch {
      message.error("Cancel failed");
    }
  }

  async function handleDelete(row: JobSummary) {
    try {
      await api.deleteTask(row.id);
      setItems((prev) => prev.filter((j) => j.id !== row.id));
      setTotal((t) => Math.max(0, t - 1));
      message.success(`Task ${row.seq ?? row.id.slice(0, 8)} deleted`);
    } catch {
      message.error("Delete failed");
    }
  }

  const scraperLabel = useMemo(() => {
    const m: Record<string, string> = {};
    scrapers.forEach((s) => (m[s.name] = s.label));
    return m;
  }, [scrapers]);

  const columns: ColumnsType<JobSummary> = [
    {
      title: <HeaderCell icon={<NumberOutlined />}>Task</HeaderCell>,
      dataIndex: "seq",
      width: 80,
      render: (_: unknown, row) => (
        <span className="crl-mono crl-muted">#{row.seq ?? row.id.slice(0, 6)}</span>
      ),
    },
    {
      title: <HeaderCell icon={<TagOutlined />}>Scraper</HeaderCell>,
      dataIndex: "scraper",
      width: 200,
      render: (name: string) => (
        <ScraperTag name={name} label={scraperLabel[name] ?? name} />
      ),
    },
    {
      title: <HeaderCell icon={<AppstoreOutlined />}>Status</HeaderCell>,
      dataIndex: "status",
      width: 140,
      render: (s: JobStatus) => <StatusPill status={s} />,
    },
    {
      title: <HeaderCell icon={<NumberOutlined />}>Items</HeaderCell>,
      width: 100,
      align: "right" as const,
      render: (_, row) => (
        <span className="crl-num">
          {row.progress.count > 0 ? row.progress.count.toLocaleString() : <span className="crl-muted">—</span>}
        </span>
      ),
    },
    {
      title: <HeaderCell icon={<ClockCircleOutlined />}>Duration</HeaderCell>,
      width: 110,
      align: "right" as const,
      render: (_, row) => (
        <span className="crl-num">{formatDuration((row.finishedAt ?? Date.now()) - row.startedAt)}</span>
      ),
    },
    {
      title: <HeaderCell icon={<CalendarOutlined />}>Started</HeaderCell>,
      width: 150,
      render: (_, row) => <span className="crl-mono">{formatDate(row.startedAt)}</span>,
    },
    {
      title: <HeaderCell icon={<CalendarOutlined />}>Finished</HeaderCell>,
      width: 150,
      render: (_, row) =>
        row.finishedAt ? (
          <span className="crl-mono">{formatDate(row.finishedAt)}</span>
        ) : (
          <span className="crl-mono crl-muted">—</span>
        ),
    },
    {
      title: <HeaderCell icon={<UserOutlined />}>Triggered by</HeaderCell>,
      width: 140,
      render: () => <span className="crl-muted">—</span>,
    },
    {
      title: "",
      width: 80,
      align: "center" as const,
      render: (_, row) => (
        <Space size={2} onClick={(e) => e.stopPropagation()}>
          {(row.status === "queued" || row.status === "running") && (
            <Button
              type="text"
              size="small"
              danger
              icon={<StopOutlined />}
              onClick={() => handleCancel(row)}
            />
          )}
          <Popconfirm
            title="Delete this task?"
            description="The result data will be permanently removed."
            okText="Delete"
            okButtonProps={{ danger: true }}
            cancelText="Cancel"
            onConfirm={() => handleDelete(row)}
          >
            <Button type="text" size="small" icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const liveInFlight = summary.live;

  return (
    <>
      <div className="crl-page-top">
        <div className="crl-breadcrumb">
          <span>Workspace</span>
          <RightOutlined className="crl-chev" style={{ fontSize: 10 }} />
          <span className="crl-cur">Tasks</span>
        </div>
        <div className="crl-page-top-spacer" />
        <Button
          type="primary"
          size="small"
          icon={<PlusOutlined />}
          onClick={() => navigate("/scrapers")}
        >
          New run
        </Button>
      </div>

      <div className="crl-page-header">
        <h1 className="crl-page-title">
          <span className="crl-title-icon">
            <UnorderedListOutlined style={{ fontSize: 15 }} />
          </span>
          Tasks
          <span className="crl-count">{summary.total.toLocaleString()} records</span>
        </h1>
      </div>

      <div className="crl-views-bar">
        {VIEWS.map((v) => (
          <button
            key={v.key}
            className={`crl-view-tab${view === v.key ? " active" : ""}`}
            onClick={() => {
              setView(v.key);
              setPage(1);
            }}
          >
            <span className="crl-dot" style={{ background: v.dot }} />
            {v.label}
            <span className="crl-vcount">{summary[v.countKey].toLocaleString()}</span>
          </button>
        ))}
        <div style={{ flex: 1 }} />
        <div className="crl-views-tools">
          <button className="crl-chip" disabled style={{ opacity: 0.55, cursor: "not-allowed" }}>
            <FilterOutlined /> Filter
          </button>
          <button className="crl-chip" disabled style={{ opacity: 0.55, cursor: "not-allowed" }}>
            <SortAscendingOutlined /> Sort
          </button>
          <button className="crl-chip" disabled style={{ opacity: 0.55, cursor: "not-allowed" }}>
            <DownloadOutlined /> Export
          </button>
        </div>
      </div>

      <div style={{ flex: 1, overflow: "auto", minHeight: 0 }}>
        <Table<JobSummary>
          className="crl-tasks-table"
          columns={columns}
          dataSource={items}
          rowKey="id"
          loading={loading}
          size="small"
          onRow={(record) => ({
            onClick: () => navigate(`/output/${record.id}`),
            style: { cursor: "pointer" },
          })}
          pagination={{
            current: page,
            pageSize: PAGE_SIZE,
            total,
            showSizeChanger: false,
            onChange: (p) => setPage(p),
            size: "small",
          }}
          scroll={{ x: "max-content" }}
        />
      </div>

      <div className="crl-footer-bar">
        <span>
          {total.toLocaleString()} records · Page {page} / {Math.max(1, Math.ceil(total / PAGE_SIZE))}
        </span>
        {liveInFlight > 0 && (
          <span>
            <span className="crl-pulse" /> {liveInFlight} in flight
          </span>
        )}
        <div className="crl-spacer" />
        <span>click row · open</span>
      </div>
    </>
  );
}

function viewToStatus(view: FilterView): JobStatus | undefined {
  if (view === "done") return "done";
  if (view === "running") return "running";
  if (view === "failed") return "failed";
  return undefined;
}

function HeaderCell({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <span style={{ color: "var(--text-tertiary)", fontSize: 11 }}>{icon}</span>
      {children}
    </span>
  );
}

function formatDate(ms: number): string {
  const d = new Date(ms);
  return d.toLocaleString(undefined, {
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}
